import type { ShopPreferencesUserPortalCustomTextDto } from "@babackapp/private-api-client";
import { normalizeLanguageCode } from "@babackapp/translations/language-utils";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { usePublicShop } from "../queries/use-public-shop";

function pickCustomText(
  customTexts: ShopPreferencesUserPortalCustomTextDto[] | undefined,
  key: string,
  targetLang: string,
): string | null {
  if (!customTexts?.length) return null;
  const entry = customTexts.find((ct) => ct.key === key);
  if (!entry?.texts?.length) return null;

  const target = normalizeLanguageCode(targetLang).toLowerCase().slice(0, 2);

  const exact = entry.texts.find(
    (t) => normalizeLanguageCode(t.lang).toLowerCase().slice(0, 2) === target &&
      (t.text ?? "").trim().length > 0,
  );
  return exact?.text ?? null;
}

function interpolate(template: string, params?: Record<string, unknown>): string {
  if (!params) return template;
  return template.replace(/\{([a-zA-Z0-9_]+)\}/g, (match, name) => {
    if (Object.prototype.hasOwnProperty.call(params, name)) {
      const value = params[name];
      return value == null ? "" : String(value);
    }
    return match;
  });
}

export function useCustomText(): (key: string, params?: Record<string, unknown>) => string {
  const { shopId } = useParams<{ shopId: string }>();
  const { data: shop } = usePublicShop(shopId!);
  const { t, i18n } = useTranslation("return-widget-v3");
  const language = i18n.language ?? "en";

  const customTexts = shop?.preferences?.userPortal?.customTexts;

  return (key: string, params?: Record<string, unknown>): string => {
    const override = pickCustomText(customTexts, key, language);
    if (override == null) {
      return String(t(key, params as never));
    }
    return interpolate(override, params);
  };
}
